import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Sparkles, Trophy, Rocket } from "lucide-react";

const CONFETTI_COLORS = [
  "#3B82F6", // blue
  "#10B981", // emerald
  "#F59E0B", // amber
  "#EC4899", // pink
  "#8B5CF6", // violet
  "#06B6D4", // cyan
];

function ConfettiPiece({ delay, startX }) {
  const color = CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)];
  const size = 8 + Math.random() * 8;
  const rotation = Math.random() * 360;
  const duration = 2 + Math.random() * 1;
  const drift = -50 + Math.random() * 100;

  return (
    <div
      className="absolute pointer-events-none"
      style={{
        left: `${startX}%`,
        top: "-20px",
        width: size,
        height: size * 0.6,
        backgroundColor: color,
        borderRadius: "2px",
        transform: `rotate(${rotation}deg)`,
        animation: `confetti-fall ${duration}s ease-out ${delay}s forwards`,
        "--drift": `${drift}px`,
      }}
    />
  );
}

function Confetti({ count = 50 }) {
  const pieces = Array.from({ length: count }, (_, i) => ({
    id: i,
    delay: Math.random() * 0.5,
    startX: 20 + Math.random() * 60,
  }));

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-[99999]">
      <style>{`
        @keyframes confetti-fall {
          0% {
            transform: translateY(0) translateX(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) translateX(var(--drift)) rotate(720deg);
            opacity: 0;
          }
        }
      `}</style>
      {pieces.map((p) => (
        <ConfettiPiece key={p.id} delay={p.delay} startX={p.startX} />
      ))}
    </div>
  );
}

function CelebrationModal({ type, onClose }) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    requestAnimationFrame(() => setShow(true));
    const timer = setTimeout(() => {
      setShow(false);
      setTimeout(onClose, 300);
    }, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const content = {
    penultimate: {
      icon: Rocket,
      title: "¡Ya casi lo tienes!",
      subtitle: "Solo te queda agendar tu llamada de consultoría",
      color: "text-brand",
      bg: "bg-brand-light",
    },
    block_complete: {
      icon: Trophy,
      title: "¡Bloque completado!",
      subtitle: "Has desbloqueado el siguiente nivel",
      color: "text-amber-500",
      bg: "bg-amber-50",
    },
    program_complete: {
      icon: Sparkles,
      title: "¡Felicidades!",
      subtitle: "Has completado todo el programa",
      color: "text-emerald-500",
      bg: "bg-emerald-50",
    },
  };

  const c = content[type] || content.block_complete;
  const Icon = c.icon;

  return (
    <div
      className={`fixed inset-0 z-[99998] flex items-center justify-center transition-opacity duration-300 ${
        show ? "opacity-100" : "opacity-0"
      }`}
    >
      <div className="absolute inset-0 bg-black/20 backdrop-blur-sm" />
      <div
        className={`relative bg-white rounded-3xl shadow-2xl p-8 max-w-sm mx-4 text-center transform transition-all duration-500 ${
          show ? "scale-100 translate-y-0" : "scale-90 translate-y-8"
        }`}
      >
        <div
          className={`w-20 h-20 ${c.bg} rounded-2xl flex items-center justify-center mx-auto mb-5`}
        >
          <Icon size={36} className={c.color} strokeWidth={1.5} />
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">{c.title}</h2>
        <p className="text-sm text-gray-500">{c.subtitle}</p>
      </div>
    </div>
  );
}

export default function Celebration({ type, onComplete }) {
  const [visible, setVisible] = useState(true);

  const handleClose = () => {
    setVisible(false);
    onComplete?.();
  };

  if (!visible) return null;

  return createPortal(
    <>
      <Confetti count={type === "program_complete" ? 80 : 50} />
      <CelebrationModal type={type} onClose={handleClose} />
    </>,
    document.body
  );
}
